import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  AlertTriangle,
  Boxes,
  CheckCheck,
  ClipboardList,
  FileDown,
  Lock,
  MapPin,
  Package,
  Printer,
  RefreshCw,
  Search,
  Tag,
  Tags,
  CalendarClock,
  ListChecks,
  PackageCheck,
  Route,
  TrendingUp,
  Waves,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useAbrirNoEdge } from '@/hooks/useAbrirNoEdge';
import type { CentroContexto } from '@/lib/tipos';
import { useSessao } from '@/hooks/useSessao';
import { useAtualizarParams, useParamUrl } from '@/hooks/useUrlEstado';
import { baixarCsv, carimboArquivo } from '@/lib/csv';
import { cn, compararNatural, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Aviso, EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Gaveta } from '@/componentes/ui/gaveta';
import {
  CabecalhoPagina,
  Campo,
  CampoComIcone,
  Esqueleto,
  Pilula,
} from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import { EmitirOnda } from '@/componentes/separacao/EmitirOnda';
import { Etiquetas } from '@/componentes/separacao/Etiquetas';
import { preverEtiquetas } from '@/lib/preverEtiquetas';
import { CabecalhoColuna, useGrade, type ColunaGrade } from '@/componentes/tabela/GradeFiltravel';

/**
 * Separação — montar a onda de picking.
 *
 * A elegibilidade é do ERP: a consulta `TELEVENDAS_ROMANEIO_SEPARACAO`, o
 * Ctrl+X do formulário `TELEVENDAS_EMISSAO` do Procfit. Nenhum pedido aparece
 * aqui que aquela regra não devolveria — isso não é preferência, é correção:
 * quem manda no ciclo do pedido é o ERP, e liberar para a rua o que ele ainda
 * segura seria o WMS mentindo sobre o estado do pedido.
 *
 * A onda se corta por SKU e mais nada. Os outros parametros da consulta
 * original (categoria, UF, cross-docking, open box) ficam sem ser informados —
 * o mesmo que deixar em branco no Procfit. Continuam na API, para o dia em que
 * a operacao precisar deles.
 *
 * O que muda é a MANEIRA de escolher. No Procfit a lista é um grid de consulta;
 * aqui ela é uma tela de montagem de onda:
 *
 *   - os contadores (pedidos, itens, SKUs, ruas) seguem a SELEÇÃO ao vivo, e
 *     não o filtro — é assim que o operador dimensiona a onda antes de soltar;
 *   - o PRAZO manda, e ele é a DATA (`DATA_SLA_MELI`), não o status. O
 *     `STATUS_SLA_MELI` do ERP só distingue "no prazo" de "atrasado", e "no
 *     prazo" junta num valor só o que vence hoje e o que vence daqui a quatro
 *     dias — separar por ele deixa na fila a ordem cuja expedição o
 *     marketplace cobra hoje. Por isso a coluna Prazo filtra por balde
 *     (vencido, vence hoje, vence amanhã, vence em N dias) e os botões de
 *     prazo SOMAM à seleção: fecha-se o de hoje e só então adianta-se o de
 *     amanhã, que é como a expedição trabalha no Procfit;
 *   - atalhos que são dinâmicas de onda de verdade: "só os monoitem" e "só o
 *     que cabe no estoque";
 *   - e o **roteiro de coleta**, que o ERP não tem: os SKUs da seleção
 *     agrupados por rua e ordenados por módulo, com o endereço e o saldo da
 *     posição. É o WMS que sabe onde o produto está, e sem isso o separador
 *     recebe uma lista de códigos e sai procurando.
 *
 * Sem descrição fixa no cabeçalho: eram duas linhas de texto explicativo
 * ocupando o topo todo dia, para quem já sabe o que a tela faz desde a segunda
 * vez que a abriu — e o topo desta tela é disputado (cabeçalho, barra da onda
 * e a barra da aba, empilhados antes do conteúdo).
 *
 * Emitir a onda grava em `TELEVENDAS_EMISSAO_COMANDAS` no ERP. Esta tela é
 * somente leitura: a emissão é passo à parte e precisa de decisão explícita.
 */

interface PedidoParaSeparar {
  televenda: number;
  empresa: number;
  pedidoInternet: string | null;
  pedidoCliente: string | null;
  /** `PEDIDO_MKT_PLACE`: o número que o parceiro e o SAC citam. */
  pedidoMarketplace: string | null;
  dataHora: string | null;
  cpfCnpj: string | null;
  cliente: string | null;
  estado: string | null;
  cidade: string | null;
  tipoEntrega: string | null;
  itens: number;
  parceiro: string | null;
  modalidade: string | null;
  tipoEntidade: string | null;
  statusSlaMeli: string | null;
  dataSlaMeli: string | null;
}

interface ItemDoPedido {
  televenda: number;
  produto: number;
  quantidade: number;
}

interface PosicaoDeColeta {
  localizador: number;
  endereco: string | null;
  corredor: string | null;
  moduloOrdem: number | null;
  disponivel: number;
}

interface SkuDaOnda {
  produto: number;
  descricao: string | null;
  /** Código de barras, já limpo pela API (o campo do ERP tem lixo). */
  ean: string | null;
  /** Na ordem de separação do PBS: BOX, PRIORIDADE, saldo, localizador. */
  posicoes: PosicaoDeColeta[];
  disponivelNoCentro: number;
}

interface DemandaSku {
  produto: number;
  descricao: string | null;
  quantidade: number;
  pedidos: number;
}

/** Um pedido procurado pelo número — e, se ele não veio, por quê. */
interface PedidoProcurado {
  termo: string;
  televenda: number | null;
  motivo: string | null;
  alerta: string | null;
}

interface OndaDisponivel {
  /** Um por número procurado, na ordem digitada. */
  procurados?: PedidoProcurado[];
  pedidos: PedidoParaSeparar[];
  itensPorPedido: ItemDoPedido[];
  catalogo: SkuDaOnda[];
  demanda: DemandaSku[];
  total: number;
  truncado: boolean;
  itens: number;
}

const ABAS = ['demanda', 'ruas', 'pedidos', 'roteiro', 'etiquetas'] as const;
type Aba = (typeof ABAS)[number];

/** O roteiro como ele ficou na hora em que a onda virou registro no ERP. */
interface PlanoEmitido {
  /** O número da emissão. É ele que casa o papel com o sistema. */
  onda: number;
  linhas: Coleta[];
  pedidos: number;
  /** Quando o plano foi calculado — o mesmo carimbo que a folha já mostrava. */
  calculadoEm: number;
}

/**
 * Dias até o prazo do marketplace.
 *
 * `DATA_SLA_MELI` vem do PBS como texto em dd/MM/aaaa; aceita ISO também, por
 * segurança. `null` = pedido sem prazo declarado, que não é o mesmo que estar
 * no prazo — e por isso não recebe selo nenhum.
 */
function diasAteOPrazo(texto: string | null): number | null {
  if (!texto) return null;
  const limpo = texto.trim().slice(0, 10);
  const br = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(limpo);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(limpo);
  const alvo = br
    ? new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]))
    : iso
      ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
      : null;
  if (!alvo || Number.isNaN(alvo.getTime())) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.round((alvo.getTime() - hoje.getTime()) / 86_400_000);
}

function selosDoPrazo(dias: number | null): { texto: string; urgente: boolean } | null {
  if (dias === null) return null;
  if (dias < 0) return { texto: `atrasado ${Math.abs(dias)}d`, urgente: true };
  if (dias === 0) return { texto: 'vence hoje', urgente: true };
  if (dias === 1) return { texto: 'vence amanhã', urgente: true };
  return { texto: `em ${dias}d`, urgente: false };
}

/**
 * O balde do prazo — e o valor pelo qual a coluna Prazo filtra.
 *
 * Por que não basta o `STATUS_SLA_MELI` do ERP: ele só sabe dizer "no prazo"
 * ou "atrasado", e "no prazo" junta num valor só o que vence HOJE e o que
 * vence daqui a quatro dias. Filtrar por ele e marcar a onda deixa na fila a
 * ordem cuja expedição o marketplace cobra hoje — que é exatamente o que se
 * quer evitar. Quem manda aqui é `DATA_SLA_MELI`, como no campo de data que a
 * expedição usa no Procfit.
 *
 * O balde é por DIA até o vencimento, e não a data crua, porque é assim que o
 * trabalho é pensado: fecha o de hoje, depois adianta o de amanhã.
 */
function grupoDoPrazo(dias: number | null): string {
  if (dias === null) return '(sem prazo)';
  if (dias < 0) return 'vencido';
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  return `vence em ${dias} dias`;
}

/**
 * As colunas do Ctrl+X, ordenáveis e filtráveis por valor.
 *
 * O pré-filtro aqui é trabalho de verdade: "só São Paulo", "só Mercado
 * Envios", "só os de hoje" — o operador fatia a fila antes de marcar, e a
 * marcação fica sobre um conjunto que ele já entendeu.
 */
const COLUNAS_PEDIDOS: ColunaGrade<PedidoParaSeparar>[] = [
  {
    chave: 'pedido',
    rotulo: 'Pedido',
    ordenar: (p) => p.televenda,
    texto: (p) => String(p.televenda),
  },
  {
    chave: 'entrada',
    rotulo: 'Entrada',
    ordenar: (p) => p.dataHora ?? '',
    // Filtro por DIA: por segundo exato cada pedido seria um valor só.
    texto: (p) => dataBr(p.dataHora).slice(0, 10),
  },
  {
    chave: 'cliente',
    rotulo: 'Cliente',
    largura: true,
    ordenar: (p) => p.cliente ?? '',
    texto: (p) => p.cliente ?? '(sem cliente)',
  },
  {
    chave: 'uf',
    rotulo: 'UF',
    ordenar: (p) => p.estado ?? '',
    texto: (p) => p.estado ?? '(sem UF)',
  },
  {
    chave: 'cidade',
    rotulo: 'Cidade',
    ordenar: (p) => p.cidade ?? '',
    texto: (p) => p.cidade ?? '(sem cidade)',
  },
  {
    chave: 'transporte',
    rotulo: 'Transporte',
    ordenar: (p) => p.tipoEntrega ?? '',
    texto: (p) => p.tipoEntrega ?? '(sem tipo)',
  },
  {
    chave: 'parceiro',
    rotulo: 'Canal',
    ordenar: (p) => canalDoParceiro(p.parceiro).nome,
    // Filtra pelo CANAL, não pela grafia: "Mercado Livre" numa caixinha só,
    // com o Full à parte porque é outra operação.
    texto: (p) => {
      const c = canalDoParceiro(p.parceiro);
      return c.full ? `${c.nome} · Full` : c.nome;
    },
  },
  {
    chave: 'prazo',
    rotulo: 'Prazo',
    // Ordena pela DATA; sem prazo declarado vai para o fim da fila.
    ordenar: (p) => p.dataSlaMeli ?? '9999-12-31',
    // Filtra pelo BALDE: no funil o operador marca "vence hoje" e recebe só
    // isso — sem o de daqui a quatro dias vindo junto.
    texto: (p) => grupoDoPrazo(diasAteOPrazo(p.dataSlaMeli)),
  },
  {
    chave: 'sla',
    rotulo: 'SLA',
    ordenar: (p) => p.statusSlaMeli ?? '',
    texto: (p) => p.statusSlaMeli ?? '(sem SLA)',
  },
  {
    chave: 'itens',
    rotulo: 'Itens',
    alinhar: 'direita',
    ordenar: (p) => p.itens,
    texto: (p) => String(p.itens),
  },
];

/**
 * Uma coleta: numa posição, deste SKU, esta quantidade.
 *
 * O grão é a POSIÇÃO, não o SKU, porque é assim que a etiqueta sai: a
 * procedure emite uma por unidade e derrama para a posição seguinte quando a
 * primeira não cobre. Um SKU pode virar três coletas em três endereços.
 */
interface Coleta {
  produto: number;
  descricao: string | null;
  localizador: number | null;
  endereco: string | null;
  corredor: string | null;
  moduloOrdem: number | null;
  /** Quanto tirar DESTA posição. */
  quantidade: number;
  /** Quanto a onda pede deste SKU no total — para dar contexto à parada. */
  totalDoSku: number;
  pedidos: number;
  /** Faltou disponível: a onda pede mais do que o centro tem. */
  descoberto: boolean;
}

/** Os números de uma lista colada, sem repetir, na ordem em que vieram. */
function numerosDe(texto: string): string[] {
  return [...new Set(texto.split(/\D+/).filter((t) => t.length > 0 && t.length <= 30))];
}

/**
 * O resultado da busca por número: quantos vieram e, dos que não vieram, por quê.
 *
 * Só ocupa espaço com o que pede ação. Os que estão na fila aparecem na lista
 * logo abaixo, e só o alerta (cancelado no ML) os repete aqui.
 */
function FaixaDosProcurados({
  procurados,
  aoVerFilaInteira,
}: {
  procurados: readonly PedidoProcurado[];
  aoVerFilaInteira: () => void;
}): JSX.Element {
  const naFila = procurados.filter((p) => p.motivo === null).length;
  const comNota = procurados.filter((p) => p.motivo !== null || p.alerta !== null);
  return (
    <div className="nao-imprimir rounded-xl border border-primaria/30 bg-primaria-suave px-3 py-2 text-[12.5px]">
      <div className="flex flex-wrap items-center gap-2">
        <Search className="h-4 w-4 shrink-0 text-primaria" />
        <span>
          {procurados.length === 1 ? (
            naFila === 1 ? (
              <>
                O pedido <b className="tabular">{procurados[0]?.termo}</b> está liberado para
                separação.
              </>
            ) : (
              <>
                O pedido <b className="tabular">{procurados[0]?.termo}</b> não está liberado para
                separação.
              </>
            )
          ) : (
            <>
              <b className="tabular">{numero(naFila)}</b> de{' '}
              <b className="tabular">{numero(procurados.length)}</b> pedidos procurados estão
              liberados para separação.
            </>
          )}
        </span>
        <button
          type="button"
          onClick={aoVerFilaInteira}
          className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-primaria transition-colors hover:bg-primaria/10"
        >
          <X className="h-3.5 w-3.5" />
          Ver a fila inteira
        </button>
      </div>
      {comNota.length > 0 ? (
        <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-auto pl-6">
          {comNota.map((p) => (
            <li key={p.termo} className="flex flex-wrap items-baseline gap-x-2">
              <span className="tabular font-mono font-bold">{p.termo}</span>
              {p.televenda !== null && String(p.televenda) !== p.termo ? (
                <span className="tabular font-mono text-[11px] text-textoFraco">
                  televenda {p.televenda}
                </span>
              ) : null}
              {p.motivo ? <span className="text-textoFraco">{p.motivo}</span> : null}
              {p.alerta ? (
                <span className="inline-flex items-center gap-1 font-semibold text-status-divergencia">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {p.alerta}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function dataBr(iso: string | null): string {
  if (!iso) return '—';
  const [data = '', hora = ''] = iso.split(' ');
  const [a, m, d] = data.split('-');
  const dia = d && m && a ? `${d}/${m}/${a}` : data;
  return hora ? `${dia} ${hora.slice(0, 5)}` : dia;
}

/**
 * O canal de venda a partir de `PARCEIROS_ECOMMERCE.NOME`.
 *
 * O nome no ERP é texto livre digitado por gente diferente ao longo dos anos:
 * o MESMO Mercado Livre aparece como "Mercado Livre Full", "Marketplace -
 * Mercadolivre" e "Marketplace - Mercado Livre  - D2C" (com espaço duplo).
 * Medido no banco em 13/09/2026, últimos 60 dias: 19 grafias para 13 canais.
 *
 * Filtrar pela coluna crua oferece 19 caixinhas onde existem 13 decisões — é o
 * mesmo defeito que o status do SLA tinha. Por isso a grafia vira CANAL, e o
 * "Full" (estoque no fulfillment do marketplace) vira marca à parte, porque é
 * operação diferente e o operador precisa distinguir as duas.
 *
 * O nome cru nunca se perde: fica no `title` do selo.
 */
interface Canal {
  /** Duas ou três letras: cabe na linha da tabela sem empurrar coluna. */
  codigo: string;
  nome: string;
  classe: string;
  full: boolean;
}

const CANAIS: readonly {
  chaves: readonly string[];
  codigo: string;
  nome: string;
  classe: string;
}[] = [
  {
    chaves: ['mercadolivre', 'mercado livre', 'meli'],
    codigo: 'ML',
    nome: 'Mercado Livre',
    classe: 'bg-[#FFE600] text-[#2D3277]',
  },
  {
    chaves: ['amazon'],
    codigo: 'AMZ',
    nome: 'Amazon',
    classe: 'bg-[#232F3E] text-[#FF9900]',
  },
  {
    chaves: ['shopee'],
    codigo: 'SHP',
    nome: 'Shopee',
    classe: 'bg-[#EE4D2D] text-white',
  },
  {
    chaves: ['magalu', 'magazine luiza'],
    codigo: 'MGL',
    nome: 'Magalu',
    classe: 'bg-[#0086FF] text-white',
  },
  {
    chaves: ['casas bahia', 'via varejo'],
    codigo: 'CB',
    nome: 'Casas Bahia',
    classe: 'bg-[#0F4C97] text-white',
  },
  {
    chaves: ['tiktok'],
    codigo: 'TT',
    nome: 'TikTok',
    classe: 'bg-[#101010] text-white',
  },
  {
    chaves: ['kabum'],
    codigo: 'KB',
    nome: 'KaBuM!',
    classe: 'bg-[#FF6500] text-white',
  },
  {
    chaves: ['inter'],
    codigo: 'INT',
    nome: 'Inter Shop',
    classe: 'bg-[#FF7A00] text-white',
  },
  {
    chaves: ['fastshop', 'fast shop'],
    codigo: 'FS',
    nome: 'Fast Shop',
    classe: 'bg-[#E30613] text-white',
  },
  {
    chaves: ['camicado'],
    codigo: 'CAM',
    nome: 'Camicado',
    classe: 'bg-[#C8102E] text-white',
  },
  {
    chaves: ['creality'],
    codigo: 'CRL',
    nome: 'Creality',
    classe: 'bg-superficie3 text-texto',
  },
  {
    chaves: ['tcl'],
    codigo: 'TCL',
    nome: 'Loja TCL',
    classe: 'bg-superficie3 text-texto',
  },
  {
    chaves: ['linx'],
    codigo: 'LNX',
    nome: 'Linx Commerce',
    classe: 'bg-superficie3 text-texto',
  },
  {
    chaves: ['vtex'],
    codigo: 'VTX',
    nome: 'Loja própria (Vtex)',
    classe: 'bg-superficie3 text-texto',
  },
];

const CANAL_PROPRIO: Canal = {
  codigo: 'MM',
  nome: 'Venda própria',
  classe: 'bg-superficie3 text-textoFraco',
  full: false,
};

function canalDoParceiro(parceiro: string | null): Canal {
  if (!parceiro?.trim()) return CANAL_PROPRIO;
  const cru = parceiro
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const full = /\bfull\b/.test(cru);
  const achado = CANAIS.find((c) => c.chaves.some((k) => cru.includes(k)));
  if (achado)
    return {
      codigo: achado.codigo,
      nome: achado.nome,
      classe: achado.classe,
      full,
    };
  // Parceiro novo: mostra as iniciais em vez de esconder o pedido atrás de um
  // traço. Canal que ninguém previu continua legível na lista.
  const limpo = cru.replace(/^marketplace\s*-\s*/, '');
  return {
    codigo: limpo.slice(0, 3).toUpperCase() || '?',
    nome: parceiro.trim(),
    classe: 'bg-superficie3 text-textoFraco',
    full,
  };
}

/** O selo do canal: cor da marca, sigla curta, nome cru no hover. */
function SeloCanal({
  parceiro,
  comNome = false,
}: {
  parceiro: string | null;
  comNome?: boolean;
}): JSX.Element {
  const canal = canalDoParceiro(parceiro);
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={parceiro?.trim() || 'Venda própria (sem parceiro de e-commerce)'}
    >
      <span
        className={cn(
          'grid h-[17px] min-w-[26px] shrink-0 place-items-center rounded px-1 text-[9.5px] font-black leading-none',
          canal.classe,
        )}
      >
        {canal.codigo}
      </span>
      {comNome ? <span className="truncate">{canal.nome}</span> : null}
      {canal.full ? (
        <span
          title="Fulfillment: o estoque fica no marketplace — este pedido é reposição para o centro deles"
          className="shrink-0 rounded bg-primaria-suave px-1 py-px text-[9px] font-bold uppercase leading-none text-primaria"
        >
          full
        </span>
      ) : null}
    </span>
  );
}

/** Hora local no formato do relogio de parede — para carimbar o plano. */
function horaBr(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Data e hora por extenso — vai para o papel, onde não há tooltip. */
function dataHoraBr(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Separacao(): JSX.Element {
  const { centro, centroAtual, empresa } = useContexto();
  const { usuario } = useSessao();

  /**
   * Filtros e aba vivem na URL, como em todas as outras telas.
   *
   * F5 não joga fora a fila que se estava montando, o botão voltar do
   * navegador funciona, e o link "olha essa fila" chega pronto pelo WhatsApp.
   * A SELEÇÃO fica de fora de propósito: onda marcada é decisão de quem está
   * na tela agora, não endereço de página — e um link que marca pedido para
   * quem abre é como se separa o que não devia.
   */
  const [skuUrl] = useParamUrl('sku');
  const [televendaUrl] = useParamUrl('televenda');
  /**
   * O DIA DA COLETA DO ML, quando a tela foi aberta a partir do painel inicial.
   *
   * Vive na URL como todo filtro daqui, e por um motivo a mais: o link é o que
   * a pessoa manda no grupo — "monta a onda de quinta" vira um endereço, não
   * uma sequência de cliques que o outro tem de repetir.
   */
  const [coletaUrl] = useParamUrl('coleta');
  /** Coleta ATÉ este dia — o "hoje / vencido" do painel inicial. */
  const [coletaAteUrl] = useParamUrl('coletaAte');
  const [abaUrl] = useParamUrl('aba', 'pedidos');
  /** Lista já emitida aberta por link (raio-X do pedido): cai em Etiquetas > Emitidas. */
  const [emissaoUrl] = useParamUrl('emissao');
  const atualizarParams = useAtualizarParams();

  const aba: Aba = ABAS.includes(abaUrl as Aba) ? (abaUrl as Aba) : 'pedidos';
  const definirAba = (a: Aba): void => atualizarParams({ aba: a === 'pedidos' ? null : a });

  // Os campos são locais enquanto se digita; viram URL só ao buscar. Digitar
  // não pode empilhar uma entrada no histórico por tecla.
  const [sku, definirSku] = React.useState(skuUrl);
  const [televenda, definirTelevenda] = React.useState(televendaUrl);
  React.useEffect(() => definirSku(skuUrl), [skuUrl]);
  React.useEffect(() => definirTelevenda(televendaUrl), [televendaUrl]);

  const [marcados, definirMarcados] = React.useState<Set<number>>(new Set());
  /** Marcados que a última busca não devolveu — avisados, não sumidos em silêncio. */
  const [perdidos, definirPerdidos] = React.useState(0);
  /** Pedido aberto na gaveta de detalhe. */
  const [detalhe, definirDetalhe] = React.useState<PedidoParaSeparar | null>(null);

  /**
   * O PLANO CONGELADO DA ONDA QUE FOI EMITIDA — e a razão de ele existir.
   *
   * O roteiro é calculado a partir da SELEÇÃO, que muda a cada clique. Enquanto
   * o papel saía da seleção, este caminho estava aberto: imprimir o roteiro,
   * entregar ao operador, desmarcar dois pedidos, emitir — e agora o papel na
   * mão de quem está andando pelo CD descreve uma onda que o sistema não tem.
   * Ninguém descobre isso no corredor; descobre na conferência, com a carga
   * montada errada.
   *
   * Por isso o papel passou a nascer do que foi GRAVADO: no instante da
   * emissão o plano é copiado para cá, com o número da onda junto, e é esta
   * cópia que exporta e imprime. Ela não muda mais, porque o que ela descreve
   * também não muda.
   */
  const [emitido, definirEmitido] = React.useState<PlanoEmitido | null>(null);

  const consulta = React.useMemo(() => {
    const f: Record<string, string> = {};
    if (skuUrl) f.produto = skuUrl;
    if (televendaUrl) f.televenda = televendaUrl;
    /* `aaaa-mm-dd` ou nada: a rota já recusa o resto, mas mandar lixo só para
       ser recusado gasta uma carga inteira da fila. */
    if (/^\d{4}-\d{2}-\d{2}$/.test(coletaUrl)) f.coletaMeli = coletaUrl;
    else if (/^\d{4}-\d{2}-\d{2}$/.test(coletaAteUrl)) f.coletaMeliAte = coletaAteUrl;
    return f;
  }, [skuUrl, televendaUrl, coletaUrl, coletaAteUrl]);

  /**
   * Abre com a fila já carregada.
   *
   * Antes a tela abria vazia e exigia um clique em "Buscar pedidos" antes de
   * qualquer trabalho — um clique e uma espera por dia, todo dia, para chegar
   * à única coisa que se vem fazer aqui. Sem SKU é o caso normal (a fila
   * inteira), e a regra do ERP já limita a 30 dias e ao teto de linhas.
   */
  const onda = useQuery<OndaDisponivel>({
    queryKey: ['separacao', empresa, centro, consulta],
    queryFn: () =>
      api.get<OndaDisponivel>('/api/separacao/pedidos', {
        centro,
        ...consulta,
      }),
    enabled: centro !== null,
    staleTime: 30_000,
  });

  const d = onda.data;

  // Espelho da seleção para o efeito abaixo ler sem virar dependência — com
  // `marcados` nas deps ele rodaria a cada clique, que é o oposto do que faz.
  const marcadosRef = React.useRef(marcados);
  marcadosRef.current = marcados;

  // Trocar de centro/empresa zera: pedido de outra empresa não separa aqui.
  React.useEffect(() => {
    definirMarcados(new Set());
    definirPerdidos(0);
  }, [empresa, centro]);

  /**
   * Nova busca PRESERVA o que continua elegível, e diz o que caiu.
   *
   * Antes a seleção era zerada a cada busca. A intenção era certa — nunca
   * separar o que a regra não devolve mais —, mas estreitar o SKU no meio da
   * montagem jogava a onda inteira fora sem uma palavra. Agora fica o que a
   * regra ainda devolve, e o que saiu vira aviso: some do conjunto, não do
   * conhecimento de quem está montando.
   */
  React.useEffect(() => {
    if (!d) return;
    const elegiveis = new Set(d.pedidos.map((p) => p.televenda));
    const antes = marcadosRef.current;
    if (antes.size === 0) return;
    const restantes = new Set([...antes].filter((t) => elegiveis.has(t)));
    if (restantes.size !== antes.size) {
      definirMarcados(restantes);
      definirPerdidos(antes.size - restantes.size);
    }
  }, [d]);

  /**
   * A onda se corta por SKU, e so.
   *
   * Os demais parametros da consulta do Procfit (categoria, UF, cross-docking,
   * open box) continuam na regra — apenas nao sao informados, que e o mesmo
   * que deixar o campo em branco la: viram NULL e nao filtram.
   *
   * `OPEN_BOX` e a excecao, porque entra na JUNCAO e nao no WHERE:
   * `ISNULL(PROD.OUTLET, 'N') = @OPENBOX`. Em branco o Procfit assume 'N', e e
   * esse o conjunto que a tela mostra.
   *
   * O custo disso, medido em 12/09/2026: de 6,3 milhoes de linhas em
   * `TELEVENDAS_PRODUTOS`, 3,65M tem OUTLET nulo e 2,63M tem 'N' — 99,7% caem
   * no padrao. Sobram 17.552 linhas com 'S' (outlet), que esta tela nao
   * mostra, exatamente como o Procfit nao mostra quando o campo fica em
   * branco. Se um dia a operacao separar outlet, o parametro ja existe na API.
   */
  const buscar = (e: React.FormEvent): void => {
    e.preventDefault();
    definirPerdidos(0);
    atualizarParams({
      sku: sku.trim() || null,
      // Lista normalizada para vírgula: é o que a URL carrega e a API lê.
      televenda: numerosDe(televenda).join(',') || null,
    });
  };

  /** Da demanda para a onda: escolher o SKU refaz a busca já filtrada nele. */
  const ondaDoSku = (produto: number): void => {
    definirPerdidos(0);
    atualizarParams({ sku: String(produto), televenda: null, aba: null });
  };

  const alternar = (televenda: number): void =>
    definirMarcados((m) => {
      const novo = new Set(m);
      if (novo.has(televenda)) novo.delete(televenda);
      else novo.add(televenda);
      return novo;
    });

  /**
   * Soma um bloco inteiro à seleção, sem tirar nada.
   *
   * É o que o shift+clique precisa: marcar de uma linha até outra é ADIÇÃO, e
   * não alternância linha a linha — senão um intervalo que cruza um pedido já
   * marcado o desmarcaria no caminho.
   */
  const adicionar = (lista: readonly PedidoParaSeparar[]): void =>
    definirMarcados((m) => {
      const novo = new Set(m);
      for (const p of lista) novo.add(p.televenda);
      return novo;
    });

  /** Quantas linhas e quantas unidades cada pedido tem, dentro da onda. */
  const porPedido = React.useMemo(() => {
    const m = new Map<number, { linhas: number; unidades: number }>();
    for (const i of d?.itensPorPedido ?? []) {
      const atual = m.get(i.televenda) ?? { linhas: 0, unidades: 0 };
      atual.linhas += 1;
      atual.unidades += i.quantidade;
      m.set(i.televenda, atual);
    }
    return m;
  }, [d]);

  /**
   * Monoitem: um SKU, uma unidade.
   *
   * É a onda que mais rende em e-commerce — separação em lote, sem conferência
   * item a item, e o pedido sai direto para embalagem. `itens` é a contagem do
   * PRÓPRIO ERP (todas as linhas do pedido), então um pedido com dois SKUs não
   * entra aqui nem que a onda só enxergue um deles.
   */
  const ehMonoitem = React.useCallback(
    (p: PedidoParaSeparar): boolean => {
      const q = porPedido.get(p.televenda);
      return p.itens === 1 && q !== undefined && q.linhas === 1 && q.unidades === 1;
    },
    [porPedido],
  );

  /**
   * Onda que cabe no estoque.
   *
   * Percorre a lista na ordem em que ela está e vai reservando: o pedido só
   * entra se TODOS os SKUs dele ainda têm saldo sobrando depois do que já foi
   * reservado pelos anteriores. Dois pedidos do mesmo SKU disputam o mesmo
   * saldo — por isso não dá para decidir pedido a pedido isoladamente.
   *
   * Sem endereço no centro (SKU que o mapa não conhece) o pedido fica de fora:
   * mandar o separador atrás do que o WMS não sabe onde está é a viagem
   * perdida que esta função existe para evitar.
   */
  const marcarQueCabe = (lista: readonly PedidoParaSeparar[]): void => {
    const sobra = new Map<number, number>();
    for (const c of d?.catalogo ?? []) sobra.set(c.produto, c.disponivelNoCentro);

    const itensDe = new Map<number, { produto: number; quantidade: number }[]>();
    for (const i of d?.itensPorPedido ?? []) {
      const atual = itensDe.get(i.televenda) ?? [];
      atual.push({ produto: i.produto, quantidade: i.quantidade });
      itensDe.set(i.televenda, atual);
    }

    const escolhidos = new Set<number>();
    for (const p of lista) {
      const itens = itensDe.get(p.televenda) ?? [];
      if (itens.length === 0) continue;
      const cabe = itens.every((i) => (sobra.get(i.produto) ?? 0) >= i.quantidade);
      if (!cabe) continue;
      for (const i of itens) sobra.set(i.produto, (sobra.get(i.produto) ?? 0) - i.quantidade);
      escolhidos.add(p.televenda);
    }
    definirMarcados(escolhidos);
  };

  /**
   * A fila vista por RUA.
   *
   * Duas leituras por corredor: os pedidos que PASSAM por ele (têm ao menos um
   * SKU lá) e os que se resolvem SÓ nele. A segunda é a onda de verdade — um
   * separador, um corredor, uma viagem —, e é ela que o botão marca.
   */
  const porRua = React.useMemo(() => {
    // A rua de um SKU e a da PRIMEIRA posicao que o ERP consumiria — e de la
    // que a coleta comeca.
    const ruaDoSku = new Map<number, string>();
    for (const c of d?.catalogo ?? []) {
      const rua = c.posicoes[0]?.corredor;
      if (rua) ruaDoSku.set(c.produto, rua);
    }

    const ruasDoPedido = new Map<number, Set<string>>();
    const unidades = new Map<string, number>();
    const skus = new Map<string, Set<number>>();

    for (const i of d?.itensPorPedido ?? []) {
      const rua = ruaDoSku.get(i.produto);
      const atual = ruasDoPedido.get(i.televenda) ?? new Set<string>();
      // Sem endereço conta como rua desconhecida: um pedido com SKU que o mapa
      // não conhece NÃO se resolve numa rua só.
      atual.add(rua ?? '\u0000');
      ruasDoPedido.set(i.televenda, atual);
      if (!rua) continue;
      unidades.set(rua, (unidades.get(rua) ?? 0) + i.quantidade);
      const s = skus.get(rua) ?? new Set<number>();
      s.add(i.produto);
      skus.set(rua, s);
    }

    const passam = new Map<string, Set<number>>();
    const exclusivos = new Map<string, Set<number>>();
    for (const [televenda, ruas] of ruasDoPedido) {
      for (const rua of ruas) {
        if (rua === '\u0000') continue;
        const p = passam.get(rua) ?? new Set<number>();
        p.add(televenda);
        passam.set(rua, p);
      }
      if (ruas.size === 1) {
        const [unica] = [...ruas];
        if (unica && unica !== '\u0000') {
          const e = exclusivos.get(unica) ?? new Set<number>();
          e.add(televenda);
          exclusivos.set(unica, e);
        }
      }
    }

    return [...unidades.keys()]
      .map((rua) => ({
        rua,
        unidades: unidades.get(rua) ?? 0,
        skus: skus.get(rua)?.size ?? 0,
        passam: passam.get(rua)?.size ?? 0,
        exclusivos: [...(exclusivos.get(rua) ?? [])],
      }))
      .sort((a, b) => b.unidades - a.unidades);
  }, [d]);

  /**
   * Soma — ou tira — um grupo inteiro da seleção, sem mexer no resto.
   *
   * É o gesto do prazo, e ele é CUMULATIVO de propósito: fecha-se o que vence
   * hoje e, se ainda couber carrinho, ADIANTA-SE o de amanhã. Os atalhos que
   * trocam a seleção inteira não servem para isso — o segundo clique jogaria
   * fora o que o primeiro marcou.
   */
  const alternarGrupo = (lista: readonly PedidoParaSeparar[]): void =>
    definirMarcados((m) => {
      const novo = new Set(m);
      const todosJaEstao = lista.length > 0 && lista.every((p) => novo.has(p.televenda));
      for (const p of lista) {
        if (todosJaEstao) novo.delete(p.televenda);
        else novo.add(p.televenda);
      }
      return novo;
    });

  const marcarTodos = (pedidos: readonly PedidoParaSeparar[]): void =>
    definirMarcados((m) => {
      const todos = pedidos.every((p) => m.has(p.televenda));
      if (todos) return new Set();
      return new Set(pedidos.map((p) => p.televenda));
    });

  /**
   * O roteiro sai da SELEÇÃO e aloca como a procedure aloca.
   *
   * Para cada SKU da onda, percorre as posições na ordem do PBS e vai
   * consumindo o disponível até cobrir a quantidade pedida. É o mesmo
   * resultado que `GERAR_ESTIQUETAS_SEPARACOES_TELEVENDAS` produziria ao
   * emitir uma etiqueta por unidade — que é a exigência: o plano impresso não
   * pode divergir das etiquetas.
   *
   * O que ainda pode divergir, e nenhuma conta resolve: o TEMPO. Entre montar
   * o roteiro e emitir, outra lista pode reservar posição. Por isso a tela diz
   * quando o plano foi calculado.
   */
  const roteiro = React.useMemo<Coleta[]>(() => {
    if (!d || marcados.size === 0) return [];

    const catalogo = new Map(d.catalogo.map((c) => [c.produto, c]));
    const pedido = new Map<number, { quantidade: number; pedidos: Set<number> }>();
    for (const i of d.itensPorPedido) {
      if (!marcados.has(i.televenda)) continue;
      const atual = pedido.get(i.produto) ?? {
        quantidade: 0,
        pedidos: new Set<number>(),
      };
      atual.quantidade += i.quantidade;
      atual.pedidos.add(i.televenda);
      pedido.set(i.produto, atual);
    }

    const coletas: Coleta[] = [];
    for (const [produto, v] of pedido) {
      const sku = catalogo.get(produto);
      let restante = v.quantidade;

      for (const pos of sku?.posicoes ?? []) {
        if (restante <= 0) break;
        const tirar = Math.min(pos.disponivel, restante);
        if (tirar <= 0) continue;
        restante -= tirar;
        coletas.push({
          produto,
          descricao: sku?.descricao ?? null,
          localizador: pos.localizador,
          endereco: pos.endereco,
          corredor: pos.corredor,
          moduloOrdem: pos.moduloOrdem,
          quantidade: tirar,
          totalDoSku: v.quantidade,
          pedidos: v.pedidos.size,
          descoberto: false,
        });
      }

      // Sobrou pedido sem posição: é a parte que o centro não cobre. Entra no
      // roteiro assim mesmo, marcada — esconder produziria um plano que fecha
      // na conta e não fecha na rua.
      if (restante > 0) {
        coletas.push({
          produto,
          descricao: sku?.descricao ?? null,
          localizador: null,
          endereco: null,
          corredor: null,
          moduloOrdem: null,
          quantidade: restante,
          totalDoSku: v.quantidade,
          pedidos: v.pedidos.size,
          descoberto: true,
        });
      }
    }

    // Ordem de caminhada: rua, depois módulo. O descoberto vai para o fim.
    return coletas.sort((a, b) => {
      if (a.descoberto !== b.descoberto) return a.descoberto ? 1 : -1;
      const rua = compararNatural(a.corredor ?? '', b.corredor ?? '');
      return rua !== 0 ? rua : (a.moduloOrdem ?? 0) - (b.moduloOrdem ?? 0);
    });
  }, [d, marcados]);

  /**
   * As etiquetas que esta onda geraria — uma por unidade dos pedidos marcados.
   *
   * Mesma fonte do roteiro (a seleção), mas grão diferente: o roteiro é por
   * PARADA (some tudo que se pega numa posição) e a etiqueta é por PEÇA, que
   * é como a esteira e a conferência trabalham.
   */
  const etiquetasDaOnda = React.useMemo(() => {
    if (!d || marcados.size === 0 || centro === null) return [];
    return preverEtiquetas({
      marcados,
      pedidos: d.pedidos,
      itens: d.itensPorPedido,
      catalogo: d.catalogo,
      centro,
    });
  }, [d, marcados, centro]);

  const resumo = React.useMemo(() => {
    const ruas = new Set(roteiro.filter((l) => l.corredor).map((l) => l.corredor));
    return {
      pedidos: marcados.size,
      itens: roteiro.reduce((a, l) => a + l.quantidade, 0),
      skus: new Set(roteiro.map((l) => l.produto)).size,
      paradas: roteiro.filter((l) => !l.descoberto).length,
      etiquetas: etiquetasDaOnda.length,
      ruas: ruas.size,
      descoberto: roteiro.filter((l) => l.descoberto).reduce((a, l) => a + l.quantidade, 0),
    };
  }, [roteiro, marcados]);

  /**
   * QUAL PLANO A ABA ROTEIRO MOSTRA — e por que a seleção tem precedência.
   *
   * Com a onda emitida e nada marcado, o que interessa é o papel dela: é o
   * fim do fluxo, e é o único que pode ser impresso. Mas se alguém já começou
   * a marcar de novo, está montando a PRÓXIMA onda, e mostrar o roteiro da
   * anterior seria mentir sobre o que está na tela. Aí volta a prévia — sem
   * exportação, porque ainda não existe onda nenhuma por trás dela.
   *
   * Limpar a seleção traz o plano emitido de volta: ele fica guardado, não se
   * perde por um clique.
   */
  const planoDaAba = React.useMemo<(Omit<PlanoEmitido, 'onda'> & { onda: number | null }) | null>(() => {
    if (marcados.size === 0) return emitido;
    return roteiro.length === 0
      ? null
      : { onda: null, linhas: roteiro, pedidos: resumo.pedidos, calculadoEm: onda.dataUpdatedAt };
  }, [marcados, emitido, roteiro, resumo.pedidos, onda.dataUpdatedAt]);

  if (empresa === null) {
    return (
      <EstadoVazio
        titulo="Escolha um centro de estoque"
        detalhe="A onda é montada por empresa, e a empresa vem do centro ativo."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-5">
      <CabecalhoPagina
        className="nao-imprimir"
        icone={<Waves className="h-5 w-5" />}
        sobrelinha={
          centroAtual
            ? `Operação · ${centroAtual.centroEstoque} ${centroAtual.nome}`
            : `Operação · empresa ${empresa}`
        }
        titulo="Separação"
        acoes={
          <form className="flex flex-wrap items-center gap-2" onSubmit={buscar}>
            <CampoComIcone
              icone={<ClipboardList className="h-4 w-4" />}
              value={televenda}
              // Aceita uma lista colada (Excel, WhatsApp): números e separadores.
              onChange={(e) => definirTelevenda(e.target.value.replace(/[^\d\s,;]/g, ''))}
              inputMode="numeric"
              placeholder="televenda ou pedido"
              aria-label="Televenda ou número do pedido no marketplace"
              title="Procura na fila inteira, não só nos pedidos carregados. Aceita a televenda ou o número do marketplace — e, se o pedido não puder separar, diz por quê."
              className="w-[190px]"
              classeCampo="tabular min-h-[36px]"
            />
            <CampoComIcone
              icone={<Package className="h-4 w-4" />}
              value={sku}
              onChange={(e) => definirSku(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              placeholder="SKU (todos)"
              aria-label="SKU da onda"
              className="w-[150px]"
              classeCampo="tabular min-h-[36px]"
            />
            <Botao type="submit" variante="primaria" tamanho="pequeno">
              <Search className="h-4 w-4" />
              Buscar
            </Botao>
          </form>
        }
      />

      {/*
        A TELA TEM DE DIZER QUE JÁ VEIO FILTRADA.

        Quem chega aqui pelo painel inicial não digitou nada, e a fila que vê é
        um recorte — 244 de 1.085, na medição do dia. Sem esta faixa, o número
        menor parece a fila inteira, e a pessoa emite a onda achando que
        limpou o dia. O botão de tirar o filtro fica ao lado do que o aplicou,
        que é onde se procura por ele.
      */}
      {consulta.coletaMeli || consulta.coletaMeliAte ? (
        <div className="nao-imprimir flex flex-wrap items-center gap-2 rounded-xl border border-primaria/30 bg-primaria-suave px-3 py-2 text-[12.5px]">
          <CalendarClock className="h-4 w-4 shrink-0 text-primaria" />
          <span>
            {consulta.coletaMeli ? (
              <>
                Mostrando só os pedidos com <b>coleta do Mercado Livre em</b>{' '}
                <b className="tabular">{formatarDiaBr(consulta.coletaMeli)}</b>
              </>
            ) : (
              <>
                Mostrando só os pedidos com <b>coleta do Mercado Livre vencida ou de hoje</b> (até{' '}
                <b className="tabular">{formatarDiaBr(consulta.coletaMeliAte ?? '')}</b>)
              </>
            )}
            {d ? (
              <>
                {' '}
                — <b className="tabular">{numero(d.total)}</b>{' '}
                {d.total === 1 ? 'pedido elegível' : 'pedidos elegíveis'} para separação
              </>
            ) : null}
            .
          </span>
          <button
            type="button"
            onClick={() => atualizarParams({ coleta: null, coletaAte: null })}
            className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-primaria transition-colors hover:bg-primaria/10"
          >
            <X className="h-3.5 w-3.5" />
            Ver a fila inteira
          </button>
        </div>
      ) : null}

      {/*
        O PEDIDO PROCURADO DIZ O QUE ACONTECEU COM ELE.
        Lista vazia não é resposta: o operador precisa saber se errou o número,
        se a etiqueta já saiu ou se o pedido está parado no ERP.
      */}
      {d?.procurados?.length ? (
        <FaixaDosProcurados
          procurados={d.procurados}
          aoVerFilaInteira={() => atualizarParams({ televenda: null })}
        />
      ) : null}

      {perdidos > 0 ? (
        <div className="nao-imprimir relative">
          <Aviso
            titulo={`${numero(perdidos)} ${
              perdidos === 1 ? 'pedido marcado saiu da fila' : 'pedidos marcados saíram da fila'
            }`}
            detalhe="O filtro mudou, ou o ERP deixou de liberá-los — etiqueta emitida em outra lista, por exemplo. O resto da sua seleção continua marcado."
          />
          <button
            type="button"
            onClick={() => definirPerdidos(0)}
            aria-label="Dispensar aviso"
            className="absolute right-2 top-2 rounded-lg p-1 text-textoFraco transition-colors hover:bg-status-atencao/15 hover:text-texto"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {onda.isLoading ? (
        <Esqueleto className="h-64" />
      ) : onda.isError ? (
        <EstadoErro erro={onda.error} aoTentarDeNovo={() => void onda.refetch()} />
      ) : d ? (
        <>
          <BarraDaOnda
            resumo={resumo}
            totalElegivel={d.total}
            carregados={d.pedidos.length}
            truncado={d.truncado}
            aba={aba}
            aoTrocarAba={definirAba}
            aoLimpar={() => definirMarcados(new Set())}
            calculadoEm={onda.dataUpdatedAt}
            atualizando={onda.isFetching}
            aoAtualizar={() => void onda.refetch()}
            emitir={
              <EmitirOnda
                empresa={empresa}
                televendas={[...marcados]}
                etiquetas={etiquetasDaOnda.length}
                sku={consulta.produto ? Number(consulta.produto) : null}
                aoEmitir={(numeroDaOnda) => {
                  /*
                    A ORDEM AQUI É A CORREÇÃO: copia, depois limpa.

                    `roteiro` sai de `marcados`. Limpar a seleção primeiro
                    apagaria o plano justamente no instante em que ele passou a
                    valer — é agora que ele deixa de ser prévia e vira o retrato
                    do que o ERP gravou.
                  */
                  definirEmitido({
                    onda: numeroDaOnda,
                    linhas: roteiro,
                    pedidos: resumo.pedidos,
                    calculadoEm: onda.dataUpdatedAt,
                  });
                  definirMarcados(new Set());
                  /*
                    E O PAPEL É O FIM DO FLUXO, não o começo.

                    A tela caía em Etiquetas, que é o outro papel. Mas o roteiro
                    é o que o operador leva para andar, e só existe a partir
                    daqui: mandar para ele fecha a montagem no lugar onde
                    imprimir deixou de ter risco. As etiquetas continuam a um
                    clique, abertas nesta mesma onda.
                  */
                  definirAba('roteiro');
                }}
              />
            }
          />

          {aba === 'demanda' ? (
            <Demanda
              linhas={d.demanda}
              catalogo={d.catalogo}
              skuAtual={consulta.produto ?? null}
              aoEscolher={ondaDoSku}
            />
          ) : aba === 'ruas' ? (
            <PorRua
              linhas={porRua}
              aoMarcar={(televendas) => {
                definirMarcados(new Set(televendas));
                definirAba('pedidos');
              }}
            />
          ) : aba === 'pedidos' ? (
            <TabelaPedidos
              pedidos={d.pedidos}
              truncado={d.truncado}
              aoProcurarNaFila={(termo) => {
                definirPerdidos(0);
                atualizarParams({ televenda: termo });
              }}
              marcados={marcados}
              aoAlternar={alternar}
              aoMarcarTodos={marcarTodos}
              aoMarcarSubconjunto={(p) => definirMarcados(new Set(p.map((x) => x.televenda)))}
              aoAlternarGrupo={alternarGrupo}
              aoMarcarIntervalo={adicionar}
              aoAbrirDetalhe={definirDetalhe}
              ehMonoitem={ehMonoitem}
              aoMarcarQueCabe={marcarQueCabe}
              aoMarcarPrimeiros={(lista, quantos) =>
                definirMarcados(new Set(lista.slice(0, quantos).map((p) => p.televenda)))
              }
            />
          ) : aba === 'etiquetas' ? (
            <Etiquetas
              empresa={empresa}
              daOnda={etiquetasDaOnda}
              pedidosMarcados={marcados.size}
              emissaoInicial={emitido?.onda ?? (Number(emissaoUrl) > 0 ? Number(emissaoUrl) : null)}
            />
          ) : planoDaAba === null ? (
            /* O roteiro sai da SELEÇÃO: sem pedido marcado não há o que caminhar. */
            <div className="cartao grid flex-1 place-items-center p-10">
              <EstadoVazio
                icone={<MapPin className="h-7 w-7" />}
                titulo="Marque os pedidos da onda"
                detalhe="O roteiro é montado a partir do que você selecionar em Pedidos — as posições saem na ordem em que o ERP as consome."
                acao={
                  <Botao
                    variante="secundaria"
                    tamanho="pequeno"
                    onClick={() => definirAba('pedidos')}
                  >
                    <ClipboardList className="h-4 w-4" />
                    Ir para os pedidos
                  </Botao>
                }
              />
            </div>
          ) : (
            <Roteiro
              onda={planoDaAba.onda}
              linhas={planoDaAba.linhas}
              pedidos={planoDaAba.pedidos}
              calculadoEm={planoDaAba.calculadoEm}
              centro={centroAtual}
              empresa={empresa}
              operador={usuario?.nome ?? null}
              aoIrParaEtiquetas={() => definirAba('etiquetas')}
            />
          )}

          {/* Detalhe do pedido: gaveta, nunca tela nova (regra 2 do blueprint). */}
          {detalhe ? (
            <DetalhePedido
              pedido={detalhe}
              itens={d.itensPorPedido.filter((i) => i.televenda === detalhe.televenda)}
              catalogo={d.catalogo}
              marcado={marcados.has(detalhe.televenda)}
              aoAlternar={() => alternar(detalhe.televenda)}
              aoFechar={() => definirDetalhe(null)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A barra da onda — os números seguem a seleção
// ---------------------------------------------------------------------------

function BarraDaOnda({
  resumo,
  totalElegivel,
  carregados,
  truncado,
  aba,
  aoTrocarAba,
  aoLimpar,
  calculadoEm,
  atualizando,
  aoAtualizar,
  emitir,
}: {
  resumo: {
    pedidos: number;
    itens: number;
    skus: number;
    paradas: number;
    ruas: number;
    descoberto: number;
    /** Quantas etiquetas esta onda geraria — uma por unidade. */
    etiquetas: number;
  };
  totalElegivel: number;
  carregados: number;
  truncado: boolean;
  aba: Aba;
  aoTrocarAba: (a: Aba) => void;
  aoLimpar: () => void;
  /** Quando esta resposta chegou do servidor (`react-query`). */
  calculadoEm: number;
  atualizando: boolean;
  aoAtualizar: () => void;
  /** O botão de emitir, montado pelo pai (sabe da sessão e da mutação). */
  emitir: React.ReactNode;
}): JSX.Element {
  const vazia = resumo.pedidos === 0;

  return (
    <div className="nao-imprimir flex flex-wrap items-center gap-x-3 gap-y-2">
      <Segmentado
        valor={aba}
        aoMudar={aoTrocarAba}
        rotuloAcessivel="Visão da separação"
        opcoes={[
          {
            valor: 'demanda',
            rotulo: 'Demanda',
            icone: <TrendingUp className="h-4 w-4" />,
          },
          {
            valor: 'ruas',
            rotulo: 'Ruas',
            icone: <Route className="h-4 w-4" />,
          },
          {
            valor: 'pedidos',
            rotulo: 'Pedidos',
            icone: <ClipboardList className="h-4 w-4" />,
            contagem: resumo.pedidos,
          },
          {
            valor: 'roteiro',
            rotulo: 'Roteiro',
            icone: <MapPin className="h-4 w-4" />,
            contagem: resumo.paradas,
          },
          {
            valor: 'etiquetas',
            rotulo: 'Etiquetas',
            icone: <Tag className="h-4 w-4" />,
            contagem: resumo.etiquetas,
          },
        ]}
      />

      {vazia ? (
        <p className="text-[13px] leading-snug text-textoFraco">
          <b className="tabular text-texto">{numero(carregados)}</b> pedidos na tela de{' '}
          <b className="tabular text-texto">{numero(totalElegivel)}</b> que o ERP libera. Marque os
          que entram na onda.
        </p>
      ) : (
        /* Com seleção os números deixam de ser legenda e viram o painel da onda:
           é por eles que se decide soltar ou cortar. */
        <div className="cartao flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5">
          <Numero rotulo="pedidos" valor={resumo.pedidos} destaque />
          <Numero rotulo="itens" valor={resumo.itens} />
          <Numero rotulo="SKUs" valor={resumo.skus} />
          <Numero rotulo="paradas" valor={resumo.paradas} />
          <Numero rotulo="ruas" valor={resumo.ruas} />
          {resumo.descoberto > 0 ? (
            <Pilula
              tom="erro"
              title="A onda pede mais do que o centro tem disponível, já descontadas as etiquetas pendentes"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {numero(resumo.descoberto)} un sem estoque
            </Pilula>
          ) : null}
        </div>
      )}

      {truncado ? (
        <Pilula tom="alerta" title="A regra devolve mais pedidos do que a tela carregou">
          lista limitada — estreite o filtro
        </Pilula>
      ) : null}

      <span className="ml-auto flex items-center gap-1.5">
        {/*
          O carimbo do plano. Entre montar a onda e emitir, OUTRA lista pode
          reservar a posição que este roteiro aponta — o disponivel já desconta
          etiqueta pendente, mas só até o instante da consulta. Dizer a hora é
          o que permite ao operador decidir se recalcula antes de imprimir.
        */}
        <button
          type="button"
          onClick={aoAtualizar}
          title={`Plano calculado às ${horaBr(calculadoEm)}. O disponível pode ter mudado desde então — clique para recalcular.`}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11.5px] text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', atualizando && 'animate-spin')} />
          {atualizando ? 'recalculando…' : `calculado às ${horaBr(calculadoEm)}`}
        </button>

        {!vazia ? (
          <>
            <Botao variante="fantasma" tamanho="pequeno" onClick={aoLimpar}>
              <X className="h-3.5 w-3.5" />
              Limpar seleção
            </Botao>
            {emitir}
          </>
        ) : null}
      </span>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex flex-col leading-none">
      <b
        className={cn(
          'tabular text-[21px] font-bold tracking-[-0.02em]',
          destaque ? 'text-primaria' : 'text-texto',
        )}
      >
        {numero(valor)}
      </b>
      <span className="rotulo mt-1">{rotulo}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pedidos: a escolha
// ---------------------------------------------------------------------------

function TabelaPedidos({
  pedidos,
  marcados,
  aoAlternar,
  aoMarcarTodos,
  aoMarcarSubconjunto,
  aoAlternarGrupo,
  aoMarcarIntervalo,
  aoAbrirDetalhe,
  ehMonoitem,
  aoMarcarQueCabe,
  aoMarcarPrimeiros,
  truncado,
  aoProcurarNaFila,
}: {
  pedidos: readonly PedidoParaSeparar[];
  /** A lista carregada é só o começo da fila (corte do TOP). */
  truncado: boolean;
  /** Leva o número ao servidor, que procura na fila inteira. */
  aoProcurarNaFila: (termo: string) => void;
  marcados: Set<number>;
  aoAlternar: (t: number) => void;
  aoMarcarTodos: (p: readonly PedidoParaSeparar[]) => void;
  aoMarcarSubconjunto: (p: readonly PedidoParaSeparar[]) => void;
  /** Soma/tira o grupo inteiro, preservando o resto da seleção. */
  aoAlternarGrupo: (p: readonly PedidoParaSeparar[]) => void;
  /** Marca um bloco de linhas de uma vez (shift+clique). */
  aoMarcarIntervalo: (p: readonly PedidoParaSeparar[]) => void;
  aoAbrirDetalhe: (p: PedidoParaSeparar) => void;
  ehMonoitem: (p: PedidoParaSeparar) => boolean;
  aoMarcarQueCabe: (p: readonly PedidoParaSeparar[]) => void;
  aoMarcarPrimeiros: (p: readonly PedidoParaSeparar[], quantos: number) => void;
}): JSX.Element {
  /**
   * Busca rápida sobre o que JÁ veio.
   *
   * Um NÚMERO que não está nas linhas carregadas pode estar depois do corte —
   * a lista traz os primeiros pelo prazo, não a fila toda. Nesse caso a busca
   * oferece ir ao servidor (ou vai direto com Enter), em vez de deixar a
   * pessoa concluir que o pedido não existe. Colar VÁRIOS números filtra a
   * lista para exatamente eles.
   */
  const [busca, definirBusca] = React.useState('');
  /** Só números (5+ dígitos) e separadores: é busca por pedido, não por texto. */
  const termos = React.useMemo(
    () => (/^[\d\s,;]+$/.test(busca.trim()) ? numerosDe(busca).filter((t) => t.length >= 5) : []),
    [busca],
  );
  const filtradas = React.useMemo(() => {
    if (termos.length > 1) {
      const alvo = new Set(termos);
      return pedidos.filter(
        (p) => alvo.has(String(p.televenda)) || (p.pedidoMarketplace !== null && alvo.has(p.pedidoMarketplace)),
      );
    }
    const q = busca.trim().toLowerCase();
    if (!q) return pedidos;
    return pedidos.filter((p) =>
      [p.televenda, p.pedidoMarketplace, p.cliente, p.cidade, p.estado, p.tipoEntrega, p.parceiro]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [pedidos, busca, termos]);

  /** Os números digitados que a lista carregada não tem. */
  const faltando = React.useMemo(() => {
    if (termos.length === 0) return [];
    const achados = new Set(
      filtradas.flatMap((p) => [String(p.televenda), p.pedidoMarketplace ?? '']),
    );
    // Um número só faz busca por "contém"; vários, por igualdade.
    if (termos.length === 1) return filtradas.length === 0 ? termos : [];
    return termos.filter((t) => !achados.has(t));
  }, [termos, filtradas]);
  const procurarFora = faltando.length > 0;

  const grade = useGrade<PedidoParaSeparar>(
    filtradas,
    COLUNAS_PEDIDOS,
    (a, b) => a.televenda - b.televenda,
  );
  const visiveis = grade.visiveis;

  const monoitem = React.useMemo(() => visiveis.filter(ehMonoitem), [visiveis, ehMonoitem]);

  /**
   * A fila fatiada por DATA de vencimento — `DATA_SLA_MELI`, o campo que a
   * expedição usa no Procfit.
   *
   * Três baldes, na ordem em que o trabalho acontece: o que já venceu, o que
   * vence hoje, o que vence amanhã. Cada um SOMA à seleção em vez de
   * substituí-la, porque adiantar o de amanhã não pode desfazer o de hoje.
   */
  const baldesDePrazo = React.useMemo(() => {
    const dias = (p: PedidoParaSeparar): number | null => diasAteOPrazo(p.dataSlaMeli);
    return [
      {
        chave: 'vencidos',
        rotulo: 'vencidos',
        urgente: true,
        dica: 'Prazo do marketplace já estourado — estes saem na frente de tudo',
        lista: visiveis.filter((p) => (dias(p) ?? 1) < 0),
      },
      {
        chave: 'hoje',
        rotulo: 'vencem hoje',
        urgente: true,
        dica: 'Expedição cobrada hoje. Feche este balde antes de adiantar qualquer outro.',
        lista: visiveis.filter((p) => dias(p) === 0),
      },
      {
        chave: 'amanha',
        rotulo: 'vencem amanhã',
        urgente: false,
        dica: 'Adiantar o de amanhã: soma à seleção, não troca a de hoje',
        lista: visiveis.filter((p) => dias(p) === 1),
      },
    ].filter((b) => b.lista.length > 0);
  }, [visiveis]);
  /** Onda do tamanho do carrinho: os N primeiros da ordem que está na tela. */
  const [tamanho, definirTamanho] = React.useState('40');

  /**
   * Âncora do shift+clique — a última linha clicada sem shift.
   *
   * Depois de ordenar por prazo, o gesto que a mão já sabe fazer é "daqui até
   * aqui": clica no primeiro, shift+clica no último. Sem isso sobra clicar 40
   * vezes, ou usar "os primeiros N" e contar de cabeça onde o bloco termina.
   *
   * Fica num ref, e não em estado, porque mudar a âncora não precisa repintar
   * a lista — e repintar 500 linhas a cada clique é o que deixa tela morosa.
   */
  const ancora = React.useRef<number | null>(null);

  const clicarLinha = (evento: React.MouseEvent, indice: number): void => {
    const base = ancora.current;
    if (evento.shiftKey && base !== null && base !== indice) {
      const [inicio, fim] = base < indice ? [base, indice] : [indice, base];
      aoMarcarIntervalo(visiveis.slice(inicio, fim + 1));
    } else {
      const linha = visiveis[indice];
      if (linha) aoAlternar(linha.televenda);
    }
    ancora.current = indice;
  };

  return (
    <div className="cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-2.5 py-1.5">
        <span className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
          <Campo
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && procurarFora) {
                e.preventDefault();
                aoProcurarNaFila(termos.join(','));
                definirBusca('');
              }
            }}
            placeholder="televenda, pedido, cliente…"
            aria-label="Procurar na lista carregada"
            className="min-h-[32px] w-[210px] pl-8 text-[12.5px]"
          />
        </span>

        {procurarFora ? (
          <Botao
            variante="primaria"
            tamanho="pequeno"
            title={
              truncado
                ? 'A lista mostra só o começo da fila. Procura este número na fila inteira — e diz por quê, se ele não puder separar.'
                : 'Procura este número no ERP e diz por que ele não está na fila.'
            }
            onClick={() => {
              aoProcurarNaFila(termos.join(','));
              definirBusca('');
            }}
          >
            <Search className="h-3.5 w-3.5" />
            {faltando.length === 1
              ? `procurar ${faltando[0]} na fila toda`
              : `${numero(faltando.length)} não estão na lista — procurar na fila toda`}
          </Botao>
        ) : null}

        <Botao variante="secundaria" tamanho="pequeno" onClick={() => aoMarcarTodos(visiveis)}>
          <CheckCheck className="h-3.5 w-3.5" />
          {visiveis.every((p) => marcados.has(p.televenda)) && visiveis.length > 0
            ? 'Desmarcar'
            : 'Marcar'}{' '}
          os {numero(visiveis.length)} da lista
        </Botao>

        {/*
          O comando do PRAZO. Cada balde é um botão que SOMA à seleção (clicar de
          novo tira), porque a expedição fecha o de hoje e só então adianta o de
          amanhã. Fatiar por status ("no prazo") misturaria o que vence hoje com
          o que vence daqui a quatro dias.
        */}
        {baldesDePrazo.length > 0 ? (
          <span className="flex items-center gap-1 rounded-xl border border-borda bg-superficie2 py-1 pl-2 pr-1">
            <CalendarClock className="h-3.5 w-3.5 text-textoFraco" />
            <span className="rotulo mr-0.5">prazo</span>
            {baldesDePrazo.map((b) => {
              const dentro = b.lista.every((p) => marcados.has(p.televenda));
              return (
                <button
                  key={b.chave}
                  type="button"
                  title={b.dica}
                  aria-pressed={dentro}
                  onClick={() => aoAlternarGrupo(b.lista)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold transition-all',
                    dentro
                      ? 'gradiente-marca text-white shadow-xs'
                      : b.urgente
                        ? 'bg-status-divergenciaFundo text-status-divergencia hover:brightness-[0.97]'
                        : 'bg-superficie text-texto ring-1 ring-inset ring-borda hover:bg-superficie3',
                  )}
                >
                  {dentro ? <CheckCheck className="h-3.5 w-3.5" /> : null}
                  {b.rotulo}
                  <span className="tabular font-bold">{numero(b.lista.length)}</span>
                </button>
              );
            })}
          </span>
        ) : null}

        {monoitem.length > 0 ? (
          <Botao
            variante="secundaria"
            tamanho="pequeno"
            title="Um SKU, uma unidade: separação em lote, sem conferência item a item"
            onClick={() => aoMarcarSubconjunto(monoitem)}
          >
            <Package className="h-3.5 w-3.5 text-primaria" />
            só os {numero(monoitem.length)} monoitem
          </Botao>
        ) : null}

        <Botao
          variante="secundaria"
          tamanho="pequeno"
          title="Reserva o saldo pedido a pedido e marca só os que o estoque do centro cobre — evita mandar o separador atrás do que não tem"
          onClick={() => aoMarcarQueCabe(visiveis)}
        >
          <PackageCheck className="h-3.5 w-3.5 text-status-livre" />
          só o que cabe no estoque
        </Botao>

        <span className="flex items-center gap-1">
          <Botao
            variante="secundaria"
            tamanho="pequeno"
            title="Marca os primeiros da ordem que está na tela — a onda do tamanho do carrinho"
            onClick={() => aoMarcarPrimeiros(visiveis, Number(tamanho) || 0)}
          >
            <ListChecks className="h-3.5 w-3.5" />
            os primeiros
          </Botao>
          <Campo
            value={tamanho}
            onChange={(e) => definirTamanho(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric"
            aria-label="Quantos pedidos marcar"
            className="tabular min-h-[32px] w-[62px] text-center text-[12.5px]"
          />
        </span>

        {!grade.intacta ? (
          <>
            <span className="text-[11.5px] text-textoFraco">
              {numero(visiveis.length)} de {numero(pedidos.length)} pela ordenação/filtro das
              colunas
            </span>
            <Botao variante="fantasma" tamanho="pequeno" onClick={grade.limpar}>
              <X className="h-3.5 w-3.5" />
              limpar colunas
            </Botao>
          </>
        ) : (
          <span className="text-[11.5px] text-textoFraco">
            ordene pela coluna, filtre pelo funil · shift+clique marca daqui até ali
          </span>
        )}

        <span className="ml-auto text-[11.5px] text-textoFraco">
          {numero(marcados.size)} marcados
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="text-left">
              <th
                scope="col"
                style={{ width: '36px' }}
                className="sticky top-0 z-20 border-b border-borda bg-superficie2 px-2.5 py-1"
              />
              {COLUNAS_PEDIDOS.map((c) => (
                <CabecalhoColuna key={c.chave} coluna={c} grade={grade} />
              ))}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p, indice) => {
              const marcado = marcados.has(p.televenda);
              const atrasado = p.statusSlaMeli?.includes('Atraso') ?? false;
              return (
                <tr
                  key={p.televenda}
                  // Shift+clique seleciona texto por padrão; num grid de
                  // marcação isso é só sujeira azul sobre a lista.
                  onMouseDown={(e) => {
                    if (e.shiftKey) e.preventDefault();
                  }}
                  onClick={(e) => clicarLinha(e, indice)}
                  className={cn(
                    'cursor-pointer border-b border-borda/60',
                    marcado ? 'bg-primaria-suave' : 'hover:bg-superficie2',
                  )}
                >
                  <td className="px-2.5 py-1">
                    <span
                      className={cn(
                        'grid h-4 w-4 place-items-center rounded border',
                        marcado
                          ? 'gradiente-marca border-transparent text-white'
                          : 'border-bordaForte',
                      )}
                    >
                      {marcado ? <CheckCheck className="h-3 w-3" /> : null}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1">
                    <span className="tabular font-mono font-bold">{p.televenda}</span>
                    {/*
                      O número do marketplace ao lado da televenda: é por ele
                      que o parceiro e o SAC chamam o pedido. Antes vinha aqui
                      `PEDIDO_INTERNET`, coluna morta desde 2019 — a linha
                      ficava sempre vazia.
                    */}
                    {p.pedidoMarketplace ? (
                      <span
                        title="Pedido no marketplace (PEDIDO_MKT_PLACE)"
                        className="tabular ml-1.5 font-mono text-[10.5px] text-textoFraco"
                      >
                        {p.pedidoMarketplace}
                      </span>
                    ) : null}
                  </td>
                  <td className="tabular whitespace-nowrap px-2.5 py-1 font-mono text-[11.5px]">
                    {dataBr(p.dataHora)}
                  </td>
                  <td className="max-w-[220px] truncate px-2.5 py-1">{p.cliente ?? '—'}</td>
                  <td className="px-2.5 py-1">
                    {p.estado ? (
                      <span className="rounded bg-superficie2 px-1 py-px text-[10px] font-bold">
                        {p.estado}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="max-w-[170px] truncate px-2.5 py-1">{p.cidade ?? '—'}</td>
                  <td className="max-w-[170px] truncate px-2.5 py-1 text-textoFraco">
                    {p.tipoEntrega ?? '—'}
                  </td>
                  <td className="max-w-[170px] px-2.5 py-1 text-textoFraco">
                    <SeloCanal parceiro={p.parceiro} comNome />
                  </td>
                  {/*
                    Prazo: o selo diz o que fazer, a data diz por quê. É este o
                    campo pelo qual a expedição fatia o dia — e não o status.
                  */}
                  <td className="whitespace-nowrap px-2.5 py-1">
                    {(() => {
                      const selo = selosDoPrazo(diasAteOPrazo(p.dataSlaMeli));
                      if (!selo) return <span className="text-textoSuave">—</span>;
                      return (
                        <span className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              'rounded px-1.5 py-0.5 text-[10.5px] font-bold',
                              selo.urgente
                                ? 'bg-status-divergenciaFundo text-status-divergencia'
                                : 'bg-superficie2 text-textoFraco',
                            )}
                          >
                            {selo.texto}
                          </span>
                          <span
                            title={`DATA_SLA_MELI: ${p.dataSlaMeli}`}
                            className="tabular font-mono text-[11px] text-textoFraco"
                          >
                            {dataBr(p.dataSlaMeli).slice(0, 10)}
                          </span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-1">
                    {p.statusSlaMeli ? (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10.5px] font-semibold',
                          atrasado
                            ? 'bg-status-divergenciaFundo text-status-divergencia'
                            : 'bg-status-livreFundo text-status-livre',
                        )}
                      >
                        {atrasado ? 'atrasado' : 'no prazo'}
                      </span>
                    ) : (
                      <span className="text-textoSuave">—</span>
                    )}
                  </td>
                  {/*
                    O número de itens é o botão do detalhe: saber o que tem
                    dentro do pedido não pode custar marcar a onda e ir até o
                    Roteiro — que aliás mostra a onda somada, nunca o pedido.
                  */}
                  <td className="px-2.5 py-1 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        aoAbrirDetalhe(p);
                      }}
                      title="Ver os SKUs e endereços deste pedido"
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-primaria-suave hover:text-primaria"
                    >
                      <span className="tabular font-bold">{p.itens}</span>
                      {ehMonoitem(p) ? (
                        <span
                          title="Monoitem: um SKU, uma unidade"
                          className="rounded bg-primaria-suave px-1 py-px text-[9.5px] font-bold uppercase text-primaria"
                        >
                          mono
                        </span>
                      ) : null}
                    </button>
                  </td>
                </tr>
              );
            })}
            {visiveis.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <p className="py-8 text-center text-textoFraco">
                    Nenhum pedido passa pelos filtros das colunas.
                  </p>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detalhe do pedido — o que tem dentro, sem sair da lista
// ---------------------------------------------------------------------------

/**
 * O conteúdo de um pedido, com o endereço de cada SKU.
 *
 * Existe porque a pergunta "o que tem dentro deste pedido?" custava caro: era
 * preciso marcar, ir ao Roteiro e olhar — e o Roteiro mostra a onda SOMADA,
 * agrupada por rua, nunca o pedido. Aqui tudo sai do que a busca já trouxe
 * (`itensPorPedido` e `catalogo`), então abrir não vai ao servidor.
 *
 * O endereço mostrado é o da PRIMEIRA posição na ordem do PBS — a que a
 * etiqueta apontaria se a onda saísse agora.
 */
function DetalhePedido({
  pedido,
  itens,
  catalogo,
  marcado,
  aoAlternar,
  aoFechar,
}: {
  pedido: PedidoParaSeparar;
  itens: readonly ItemDoPedido[];
  catalogo: readonly SkuDaOnda[];
  marcado: boolean;
  aoAlternar: () => void;
  aoFechar: () => void;
}): JSX.Element {
  const porProduto = React.useMemo(() => new Map(catalogo.map((c) => [c.produto, c])), [catalogo]);
  const unidades = itens.reduce((a, i) => a + i.quantidade, 0);
  const selo = selosDoPrazo(diasAteOPrazo(pedido.dataSlaMeli));

  return (
    <Gaveta
      aberta
      aoFechar={aoFechar}
      titulo={
        <span className="flex items-center gap-2">
          <span className="tabular font-mono">{pedido.televenda}</span>
          <SeloCanal parceiro={pedido.parceiro} />
        </span>
      }
      subtitulo={`${pedido.cliente ?? 'sem cliente'} · ${pedido.cidade ?? '—'}/${
        pedido.estado ?? '—'
      }`}
      acoes={
        <Botao
          variante={marcado ? 'suave' : 'primaria'}
          tamanho="pequeno"
          onClick={aoAlternar}
          title={marcado ? 'Tirar este pedido da onda' : 'Pôr este pedido na onda'}
        >
          <CheckCheck className="h-3.5 w-3.5" />
          {marcado ? 'Na onda' : 'Marcar'}
        </Botao>
      }
    >
      <div className="border-b border-borda bg-superficie2 px-5 py-3">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-textoFraco">
          {selo ? (
            <span
              title={`DATA_SLA_MELI: ${pedido.dataSlaMeli}`}
              className={cn(
                'rounded px-1.5 py-0.5 text-[10.5px] font-bold',
                selo.urgente
                  ? 'bg-status-divergenciaFundo text-status-divergencia'
                  : 'bg-superficie3 text-textoFraco',
              )}
            >
              {selo.texto} · {dataBr(pedido.dataSlaMeli).slice(0, 10)}
            </span>
          ) : null}
          <span>
            entrada <b className="tabular text-texto">{dataBr(pedido.dataHora)}</b>
          </span>
          {pedido.tipoEntrega ? <span>{pedido.tipoEntrega}</span> : null}
        </p>
        <p className="mt-1.5 text-[12px] text-textoFraco">
          <b className="tabular text-texto">{numero(itens.length)}</b>{' '}
          {itens.length === 1 ? 'linha' : 'linhas'} ·{' '}
          <b className="tabular text-texto">{numero(unidades)}</b>{' '}
          {unidades === 1 ? 'unidade' : 'unidades'}
          {pedido.itens !== itens.length ? (
            <span
              title="O pedido tem mais linhas no ERP do que a onda enxerga — as outras estão fora do filtro atual (open box, por exemplo)."
              className="ml-1.5 rounded bg-status-atencaoFundo px-1.5 py-0.5 text-[10.5px] font-semibold text-status-atencao"
            >
              {numero(pedido.itens)} no ERP
            </span>
          ) : null}
        </p>
      </div>

      <div className="grid gap-1.5 p-3">
        {itens.map((i) => {
          const sku = porProduto.get(i.produto);
          const posicao = sku?.posicoes[0];
          const cobre = (sku?.disponivelNoCentro ?? 0) >= i.quantidade;
          return (
            <div
              key={i.produto}
              className="flex items-center gap-2.5 rounded-lg border border-borda bg-superficie px-2.5 py-2"
            >
              <ImagemProduto sku={i.produto} tamanho={38} />
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block font-mono text-[12.5px] font-bold">{i.produto}</span>
                <span className="block truncate text-[11.5px] text-textoFraco">
                  {sku?.descricao ?? 'sem descrição'}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                  {posicao ? (
                    <>
                      <MapPin className="h-3 w-3 text-primaria" />
                      <b className="font-mono">{posicao.endereco ?? posicao.localizador}</b>
                      <span className="text-textoFraco">
                        {numero(posicao.disponivel)} disp. nesta posição
                      </span>
                    </>
                  ) : (
                    <span className="flex items-center gap-1 font-semibold text-status-divergencia">
                      <AlertTriangle className="h-3 w-3" />
                      sem endereço no centro
                    </span>
                  )}
                </span>
              </span>
              <span className="shrink-0 text-right leading-none">
                <b className="tabular text-[17px] font-bold">{numero(i.quantidade)}</b>
                <span className="rotulo mt-1 block">a coletar</span>
                {posicao && !cobre ? (
                  <span
                    title="O centro inteiro não cobre esta quantidade, já descontadas as etiquetas pendentes"
                    className="mt-1 block rounded bg-status-divergenciaFundo px-1 py-px text-[9.5px] font-bold uppercase text-status-divergencia"
                  >
                    falta
                  </span>
                ) : null}
              </span>
            </div>
          );
        })}
        {itens.length === 0 ? (
          <p className="p-6 text-center text-[13px] text-textoFraco">
            Nenhuma linha deste pedido no filtro atual.
          </p>
        ) : null}
      </div>
    </Gaveta>
  );
}

// ---------------------------------------------------------------------------
// Demanda — a consulta auxiliar que ajuda a escolher a onda
// ---------------------------------------------------------------------------

/**
 * O que está esperando separação, por SKU.
 *
 * Não é histórico de vendas: é a fila elegível de AGORA, agregada pelo mesmo
 * conjunto do Ctrl+X. Para montar onda é essa a pergunta certa — o que vendeu
 * mês passado não diz o que há para separar hoje.
 *
 * Clicar numa linha monta a onda daquele SKU: é o caminho que o operador faz
 * de cabeça ("o que tem mais? então separo esse primeiro").
 */
/**
 * A ficha do SKU que abre ao passar o mouse na Demanda.
 *
 * Escolher o SKU da onda é uma decisão que se toma olhando a lista inteira —
 * parar, clicar, ler e voltar quebra a comparação. Por isso a ficha é hover, e
 * não clique: foto grande para reconhecer a caixa, EAN para conferir na mão, e
 * as CINCO primeiras posições na ordem em que o ERP as consome, com o
 * disponível de cada uma. Cinco porque é o que responde "isso sai de um lugar
 * só ou vira caça ao tesouro?" — que é a pergunta que decide a onda.
 *
 * Tudo sai do catálogo que a busca já trouxe: passar o mouse não vai ao
 * servidor.
 */
function FichaSku({
  sku,
  demanda,
}: {
  sku: SkuDaOnda | undefined;
  demanda: DemandaSku;
}): JSX.Element {
  const posicoes = (sku?.posicoes ?? []).slice(0, 5);
  const restantes = (sku?.posicoes.length ?? 0) - posicoes.length;
  const cobre = (sku?.disponivelNoCentro ?? 0) >= demanda.quantidade;

  return (
    <div className="w-[340px] max-w-[92vw] overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte">
      <div className="flex gap-3 border-b border-borda bg-superficie2 p-3">
        <ImagemProduto sku={demanda.produto} tamanho={92} className="shrink-0 border-borda" />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block text-[13px] font-bold leading-snug">
            {sku?.descricao ?? demanda.descricao ?? `SKU ${demanda.produto}`}
          </span>
          <span className="tabular mt-1 block font-mono text-[11.5px] text-textoFraco">
            SKU {demanda.produto}
          </span>
          {sku?.ean ? (
            <span
              title="Código de barras (CADASTROS_PRODUTO_SKUS.EAN)"
              className="tabular mt-1 inline-block rounded bg-superficie3 px-1.5 py-0.5 font-mono text-[11px] font-bold"
            >
              {sku.ean}
            </span>
          ) : (
            <span className="mt-1 block text-[10.5px] text-textoSuave">sem EAN cadastrado</span>
          )}
        </span>
      </div>

      <div className="flex items-center gap-2 border-b border-borda px-3 py-1.5 text-[11.5px]">
        <span>
          fila pede <b className="tabular text-texto">{numero(demanda.quantidade)}</b> un
        </span>
        <span className="text-textoSuave">·</span>
        <span className={cn(cobre ? 'text-status-livre' : 'text-status-divergencia')}>
          centro tem <b className="tabular">{numero(sku?.disponivelNoCentro ?? 0)}</b>
        </span>
      </div>

      <div className="p-2">
        <p className="rotulo mb-1.5 px-1">onde coletar · ordem do ERP</p>
        {posicoes.length === 0 ? (
          <p className="flex items-center gap-1.5 px-1 py-2 text-[12px] font-semibold text-status-divergencia">
            <AlertTriangle className="h-3.5 w-3.5" />
            Sem endereço no centro — o mapa não sabe onde este SKU está.
          </p>
        ) : (
          <ol className="grid gap-1">
            {posicoes.map((pos, i) => (
              <li
                key={pos.localizador}
                className="flex items-center gap-2 rounded-lg border border-borda bg-superficie px-2 py-1"
              >
                <span className="tabular grid h-[18px] w-[18px] shrink-0 place-items-center rounded bg-primaria-suave text-[10px] font-bold text-primaria">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate font-mono text-[12px] font-bold">
                    {pos.endereco ?? `localizador ${pos.localizador}`}
                  </span>
                  <span className="tabular block font-mono text-[10px] text-textoFraco">
                    {pos.corredor ?? '—'} · loc {pos.localizador}
                  </span>
                </span>
                <span className="shrink-0 text-right leading-none">
                  <b className="tabular text-[14px]">{numero(pos.disponivel)}</b>
                  <span className="rotulo mt-0.5 block">disp</span>
                </span>
              </li>
            ))}
          </ol>
        )}
        {restantes > 0 ? (
          <p className="px-1 pt-1.5 text-[10.5px] text-textoFraco">
            + {numero(restantes)} {restantes === 1 ? 'outra posição' : 'outras posições'} com saldo
          </p>
        ) : null}
      </div>
    </div>
  );
}

function Demanda({
  linhas,
  catalogo,
  skuAtual,
  aoEscolher,
}: {
  linhas: readonly DemandaSku[];
  catalogo: readonly SkuDaOnda[];
  skuAtual: string | null;
  aoEscolher: (produto: number) => void;
}): JSX.Element {
  const porProduto = React.useMemo(() => new Map(catalogo.map((c) => [c.produto, c])), [catalogo]);
  const [busca, definirBusca] = React.useState('');
  const visiveis = React.useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return linhas;
    return linhas.filter(
      (l) => String(l.produto).includes(t) || (l.descricao ?? '').toLowerCase().includes(t),
    );
  }, [linhas, busca]);

  const maior = linhas[0]?.quantidade ?? 1;

  return (
    <div className="cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-2.5 py-1.5">
        <p className="text-[12.5px]">
          <b>{numero(linhas.length)} SKUs</b> esperando separação
        </p>
        <span className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
          <Campo
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="SKU ou descrição"
            aria-label="Procurar SKU na demanda"
            className="min-h-[32px] w-[240px] pl-8 text-[12.5px]"
          />
        </span>
        <span className="ml-auto text-[11.5px] text-textoFraco">
          clique num SKU para montar a onda dele
        </span>
      </div>

      {/*
        `delayDuration` curto o bastante para a ficha parecer resposta ao gesto,
        e longo o bastante para ela não piscar quando o mouse só atravessa a
        lista a caminho de outra coisa.
      */}
      <Tooltip.Provider delayDuration={160} skipDelayDuration={400}>
        <div className="min-h-0 flex-1 overflow-auto p-2">
          {visiveis.map((l, i) => {
            const atual = skuAtual === String(l.produto);
            return (
              <Tooltip.Root key={l.produto}>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    onClick={() => aoEscolher(l.produto)}
                    className={cn(
                      'mb-1 flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                      atual
                        ? 'border-primaria bg-primaria-suave'
                        : 'border-borda bg-superficie hover:border-primaria/40 hover:bg-primaria-suave',
                    )}
                  >
                    <span className="tabular w-6 shrink-0 text-right text-[11px] font-bold text-textoSuave">
                      {i + 1}
                    </span>
                    <ImagemProduto sku={l.produto} tamanho={32} />
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-[12.5px] font-semibold">
                        {l.descricao ?? `SKU ${l.produto}`}
                      </span>
                      <span className="block text-[10.5px] text-textoFraco">
                        SKU {l.produto} · {numero(l.pedidos)}{' '}
                        {l.pedidos === 1 ? 'pedido' : 'pedidos'}
                      </span>
                    </span>
                    {/* Barra proporcional: dá a leitura de "quem domina a fila" sem
                  precisar comparar número a número. */}
                    <span className="hidden h-1.5 w-[140px] shrink-0 overflow-hidden rounded-full bg-superficie2 sm:block">
                      <span
                        className="gradiente-marca block h-full rounded-full"
                        style={{
                          width: `${Math.max(4, (l.quantidade / maior) * 100)}%`,
                        }}
                      />
                    </span>
                    <span className="w-16 shrink-0 text-right leading-tight">
                      <b className="tabular block text-[15px] text-primaria">
                        {numero(l.quantidade)}
                      </b>
                      <span className="block text-[10px] uppercase text-textoFraco">un</span>
                    </span>
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  {/*
                    Ancorada na VERTICAL, e não à direita: o gatilho é a linha
                    inteira, que ocupa toda a largura — não sobra espaço lateral
                    nenhum, e o Radix virava para a esquerda e a ficha saía da
                    tela. Em cima/embaixo sempre cabe, e `align="start"` prende
                    a borda esquerda da ficha à da linha.

                    `pointerEvents: none` porque a ficha é só leitura: assim o
                    mouse a atravessa e chega na linha de baixo, em vez de
                    "sair do gatilho" e fazer a ficha piscar.
                  */}
                  <Tooltip.Content
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    collisionPadding={12}
                    style={{ pointerEvents: 'none' }}
                    className="z-50 data-[state=delayed-open]:animate-aparecer"
                  >
                    <FichaSku sku={porProduto.get(l.produto)} demanda={l} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            );
          })}

          {visiveis.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-textoFraco">
              Nenhum SKU corresponde à procura.
            </p>
          ) : null}
        </div>
      </Tooltip.Provider>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ruas — a onda de um corredor só
// ---------------------------------------------------------------------------

interface ResumoRua {
  rua: string;
  unidades: number;
  skus: number;
  passam: number;
  exclusivos: number[];
}

/**
 * A fila vista por corredor.
 *
 * "Passam" é quem tem ao menos um SKU na rua; "só nesta" é quem se resolve
 * inteiro ali. A onda que rende é a segunda: o separador entra no corredor uma
 * vez e sai com os pedidos fechados. A primeira aparece para dar a dimensão —
 * um número alto de "passam" com poucos "só nesta" quer dizer fila espalhada,
 * e aí a onda por SKU rende mais que a onda por rua.
 */
function PorRua({
  linhas,
  aoMarcar,
}: {
  linhas: readonly ResumoRua[];
  aoMarcar: (televendas: number[]) => void;
}): JSX.Element {
  const maior = linhas[0]?.unidades ?? 1;

  return (
    <div className="cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-2.5 py-1.5">
        <p className="text-[12.5px]">
          <b>{numero(linhas.length)} ruas</b> com trabalho represado
        </p>
        <span className="ml-auto text-[11.5px] text-textoFraco">
          marcar puxa os pedidos que se resolvem só naquela rua
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {linhas.map((l) => (
          <div
            key={l.rua}
            className="mb-1 flex items-center gap-2.5 rounded-lg border border-borda bg-superficie px-2.5 py-1.5"
          >
            <MapPin className="h-4 w-4 shrink-0 text-primaria" />
            <span className="w-[92px] shrink-0 font-mono text-[13px] font-bold">{l.rua}</span>
            <span className="min-w-0 flex-1 text-[11.5px] text-textoFraco">
              {numero(l.skus)} SKUs · {numero(l.passam)}{' '}
              {l.passam === 1 ? 'pedido passa' : 'pedidos passam'} aqui ·{' '}
              <b className="text-texto">{numero(l.exclusivos.length)}</b> se{' '}
              {l.exclusivos.length === 1 ? 'resolve' : 'resolvem'} só nesta rua
            </span>
            <span className="hidden h-1.5 w-[120px] shrink-0 overflow-hidden rounded-full bg-superficie2 sm:block">
              <span
                className="gradiente-marca block h-full rounded-full"
                style={{ width: `${Math.max(4, (l.unidades / maior) * 100)}%` }}
              />
            </span>
            <span className="w-14 shrink-0 text-right leading-tight">
              <b className="tabular block text-[15px] text-primaria">{numero(l.unidades)}</b>
              <span className="block text-[10px] uppercase text-textoFraco">un</span>
            </span>
            <Botao
              variante="secundaria"
              tamanho="micro"
              disabled={l.exclusivos.length === 0}
              onClick={() => aoMarcar(l.exclusivos)}
            >
              marcar {numero(l.exclusivos.length)}
            </Botao>
          </div>
        ))}

        {linhas.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-textoFraco">
            Nenhum SKU da fila tem endereço no centro ativo.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roteiro de coleta — o que o WMS acrescenta
// ---------------------------------------------------------------------------

/**
 * O roteiro de coleta — prévia na tela, papel só depois de emitir.
 *
 * `onda === null` é a PRÉVIA: serve para dimensionar a onda, ver quantas ruas
 * ela cruza e decidir se está do tamanho do carrinho. Ela não exporta e não
 * imprime, e isso é o ponto da tela, não uma limitação dela.
 *
 * O motivo é um caminho que existia e dava errado em silêncio: imprimir o
 * roteiro, entregar ao operador, mexer na seleção, emitir. O papel andava pelo
 * CD descrevendo uma onda que o ERP nunca gravou, e a divergência só aparecia
 * na conferência. Papel não se desfaz com Ctrl+Z.
 *
 * Por isso a folha tem dono: ela só existe com um número de onda impresso nela,
 * e esse número vem da emissão. Note que a classe `folha` também só entra aí —
 * sem ela o Ctrl+P do navegador não acha o que imprimir, então o bloqueio vale
 * para o atalho, e não só para o botão.
 */
function Roteiro({
  onda,
  linhas,
  pedidos,
  calculadoEm,
  centro,
  empresa,
  operador,
  aoIrParaEtiquetas,
}: {
  /** A emissão que este plano representa. `null` = prévia, não imprime. */
  onda: number | null;
  linhas: readonly Coleta[];
  pedidos: number;
  calculadoEm: number;
  centro: CentroContexto | null;
  empresa: number | null;
  operador: string | null;
  aoIrParaEtiquetas: () => void;
}): JSX.Element {
  const porRua = React.useMemo(() => {
    const grupos = new Map<string, Coleta[]>();
    for (const l of linhas) {
      const chave = l.corredor ?? '(sem estoque disponível)';
      const atual = grupos.get(chave) ?? [];
      atual.push(l);
      grupos.set(chave, atual);
    }
    return [...grupos.entries()];
  }, [linhas]);

  /* O nome do arquivo carrega a onda: duas planilhas na pasta de Downloads no
     mesmo dia só se distinguem por ele. */
  const exportar = (): void =>
    baixarCsv(`roteiro-onda-${onda ?? 'previa'}-${carimboArquivo()}`, linhas, [
      { cabecalho: 'Rua', valor: (l) => l.corredor },
      { cabecalho: 'Módulo', valor: (l) => l.moduloOrdem },
      { cabecalho: 'Endereço', valor: (l) => l.endereco },
      { cabecalho: 'Localizador', valor: (l) => l.localizador, texto: true },
      { cabecalho: 'SKU', valor: (l) => l.produto, texto: true },
      { cabecalho: 'Descrição', valor: (l) => l.descricao },
      { cabecalho: 'Coletar aqui', valor: (l) => l.quantidade },
      { cabecalho: 'Total do SKU na onda', valor: (l) => l.totalDoSku },
      { cabecalho: 'Pedidos', valor: (l) => l.pedidos },
      { cabecalho: 'Sem estoque', valor: (l) => (l.descoberto ? 'SIM' : '') },
    ]);

  const emitido = onda !== null;
  // O papel sai pelo Edge, com prévia e escolha de impressora (ver abrirPapelNoEdge).
  const edge = useAbrirNoEdge();
  const folha = React.useRef<HTMLDivElement>(null);

  return (
    <div
      ref={folha}
      className={cn(
        'cartao flex min-h-0 flex-1 flex-col overflow-hidden p-0',
        /* `folha` é o que a regra de impressão enxerga. Prévia não é folha. */
        emitido && 'folha',
      )}
    >
      <div className="cabecalho-folha border-b border-borda px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[14px] font-bold">
            Roteiro de coleta
            {emitido ? (
              <span className="tabular ml-2 rounded-md bg-primaria/10 px-2 py-0.5 text-[12px] font-bold text-primaria">
                onda {onda}
              </span>
            ) : null}
          </p>
          <p className="text-[12px] text-textoFraco">
            {numero(linhas.length)} paradas · {numero(new Set(linhas.map((l) => l.produto)).size)}{' '}
            SKUs · {numero(porRua.length)} ruas · na ordem de caminhada
          </p>
          <span className="nao-imprimir ml-auto flex items-center gap-2">
            {emitido ? (
              <>
                <Botao variante="secundaria" tamanho="pequeno" onClick={aoIrParaEtiquetas}>
                  <Tags className="h-3.5 w-3.5" />
                  Etiquetas
                </Botao>
                <Botao variante="secundaria" tamanho="pequeno" onClick={exportar}>
                  <FileDown className="h-3.5 w-3.5" />
                  Excel / CSV
                </Botao>
                <Botao
                  variante="primaria"
                  tamanho="pequeno"
                  onClick={() =>
                    folha.current &&
                    void edge.abrir({ modo: 'pagina', conteudo: folha.current, titulo: `Roteiro onda ${onda}` })
                  }
                  carregando={edge.abrindo}
                  title="Abre o roteiro no Edge, com a prévia e a escolha da impressora A4."
                >
                  <Printer className="h-3.5 w-3.5" />
                  Imprimir
                </Botao>
              </>
            ) : (
              <span className="flex items-center gap-1.5 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-2.5 py-1.5 text-[11.5px] leading-snug text-textoFraco">
                <Lock className="h-3.5 w-3.5 shrink-0 text-status-atencao" />
                <span>
                  <b className="text-texto">Prévia — ainda não dá para imprimir.</b> Emita a onda
                  primeiro; o papel sai com o número dela.
                </span>
              </span>
            )}
          </span>
        </div>
        {edge.erro ? (
          <p className="nao-imprimir mt-1.5 rounded-lg bg-status-divergenciaFundo px-3 py-1.5 text-[12px] text-status-divergencia">
            {edge.erro}
          </p>
        ) : null}

        {/*
          A identificação da folha. No papel não há tooltip nem barra de
          contexto: duas ondas impressas na mesma mesa, sem centro, hora e
          quem montou, viram uma só — e aí se separa a lista errada. A hora
          também é o que permite perceber que o papel está velho em relação
          ao estoque.
        */}
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-textoFraco">
          {/* No papel, o número da onda é o que permite voltar ao sistema e
              conferir que a folha e o registro falam do mesmo lote. É o
              primeiro dado da linha de propósito. */}
          {emitido ? (
            <span>
              Onda <b className="tabular text-texto">{onda}</b>
            </span>
          ) : null}
          <span>
            Centro <b className="tabular text-texto">{centro?.centroEstoque ?? '—'}</b>
            {centro?.nome ? ` · ${centro.nome}` : ''}
            {empresa !== null ? ` · empresa ${empresa}` : ''}
          </span>
          <span>
            <b className="tabular text-texto">{numero(pedidos)}</b>{' '}
            {pedidos === 1 ? 'pedido' : 'pedidos'} ·{' '}
            <b className="tabular text-texto">
              {numero(linhas.reduce((a, l) => a + l.quantidade, 0))}
            </b>{' '}
            unidades
          </span>
          <span>
            Plano calculado em <b className="tabular text-texto">{dataHoraBr(calculadoEm)}</b>
          </span>
          {operador ? (
            <span>
              Montado por <b className="text-texto">{operador}</b>
            </span>
          ) : null}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {porRua.map(([rua, itens]) => (
          <section key={rua} className="mb-4">
            <p className="mb-1.5 flex items-center gap-2 border-b border-borda pb-1">
              <MapPin className="h-4 w-4 text-primaria" />
              <b className="font-mono text-[14px]">RUA {rua}</b>
              <span className="text-[11.5px] text-textoFraco">
                {itens.length} {itens.length === 1 ? 'parada' : 'paradas'} ·{' '}
                {numero(itens.reduce((a, l) => a + l.quantidade, 0))} un
              </span>
            </p>

            <div className="grid gap-1.5">
              {itens.map((l) => {
                // Uma parada pode repetir o SKU em posicoes diferentes: a
                // chave e o par produto+localizador.
                const parcial = l.quantidade < l.totalDoSku;
                return (
                  <div
                    key={`${l.produto}:${l.localizador ?? 'sem'}`}
                    className="flex items-center gap-2.5 rounded-lg border border-borda bg-superficie px-2.5 py-1.5"
                  >
                    <ImagemProduto sku={l.produto} tamanho={34} />
                    <span className="min-w-[132px] leading-tight">
                      <span className="block font-mono text-[13px] font-bold">
                        {l.endereco ?? 'sem estoque'}
                      </span>
                      <span className="block text-[10.5px] text-textoFraco">
                        {l.localizador ? `LOC ${l.localizador}` : 'nenhuma posição disponível'}
                      </span>
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">
                      <span className="block truncate text-[12.5px] font-semibold">
                        {l.descricao ?? `SKU ${l.produto}`}
                      </span>
                      <span className="block text-[10.5px] text-textoFraco">
                        SKU {l.produto} · {l.pedidos} {l.pedidos === 1 ? 'pedido' : 'pedidos'}
                        {parcial ? (
                          <span className="ml-1 font-semibold text-status-atencao">
                            parte de {numero(l.totalDoSku)} un — o resto sai de outra posição
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="text-right leading-tight">
                      <b className="tabular block text-[17px] text-primaria">
                        {numero(l.quantidade)}
                      </b>
                      <span className="block text-[10px] uppercase text-textoFraco">coletar</span>
                    </span>
                    {l.descoberto ? (
                      <span
                        title="O centro não tem disponível para esta parte da onda, já descontadas as etiquetas pendentes de outras listas"
                        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-status-divergenciaFundo px-1.5 py-0.5 text-[10.5px] font-bold text-status-divergencia"
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                        sem estoque
                      </span>
                    ) : parcial ? (
                      <span
                        title="Esta é uma das posições deste SKU na onda"
                        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-superficie2 px-1.5 py-0.5 text-[10.5px] font-semibold text-textoFraco"
                      >
                        <Boxes className="h-3.5 w-3.5" />
                        parcial
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {linhas.length === 0 ? (
          <EstadoVazio
            icone={<Package className="h-7 w-7" />}
            titulo="Nenhum pedido marcado"
            detalhe="Volte para a lista e marque os pedidos que entram nesta onda."
          />
        ) : null}
      </div>
    </div>
  );
}

/** `2026-09-18` -> `18/09/2026`, sem `new Date` reinterpretando o fuso. */
function formatarDiaBr(iso: string): string {
  const [a, m, d] = iso.split('-');
  return d === undefined ? iso : `${d}/${m}/${a}`;
}
